// src/pages/StaffPortal.js
// ──────────────────────────────────────────────────────────────────────────
// Staff portal — dashboard shell. Staff manage classes, enrol students, and
// post announcements & assignments to a class. Requires staff/admin role.
// ──────────────────────────────────────────────────────────────────────────

import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import PortalShell from '../components/portal/PortalShell';
import { useAuth } from '../context/AuthContext';
import {
  fetchClasses, saveClass, removeClass,
  fetchEnrolments, enrolStudent, unenrol,
  fetchAnnouncements, saveAnnouncement, removeAnnouncement,
  fetchAssignments, saveAssignment, removeAssignment, uploadFile,
  fetchSubmissionsForAssignment, gradeSubmission,
} from '../lib/cms';
import theme from '../theme/theme';

const Note = styled.div`
  max-width: 520px; margin: 5rem auto; text-align: center; padding: 0 1.5rem;
  h2 { font-weight: 400; font-size: 1.8rem; } p { color: ${theme.color.inkSoft}; margin: 0.6rem 0 1.4rem; }
  a { background: ${theme.color.accent}; color: #fff; font-weight: 700; padding: 12px 26px; border-radius: ${theme.layout.radius}; }
`;
const H = styled.h2` font-weight: 400; font-size: 1.5rem; margin-bottom: 1rem; `;
const Empty = styled.p` color: ${theme.color.inkSoft}; `;
const Card = styled.div` border: 1px solid ${theme.color.line}; border-radius: ${theme.layout.radius}; padding: 1.5rem; background: #fff; margin-bottom: 1.4rem; `;
const Field = styled.div`
  margin-bottom: 0.9rem;
  label { display: block; font-size: 0.74rem; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; color: ${theme.color.inkSoft}; margin-bottom: 0.35rem; }
  input, textarea, select { width: 100%; font-family: ${theme.font.body}; font-size: 0.95rem; padding: 10px 13px; border: 1px solid ${theme.color.line}; border-radius: ${theme.layout.radius}; background: #fff; color: ${theme.color.ink}; }
  input:focus, textarea:focus { outline: none; border-color: ${theme.color.accent}; } textarea { min-height: 90px; resize: vertical; }
`;
const Btn = styled.button`
  background: ${(p) => (p.$ghost ? '#fff' : theme.color.accent)}; color: ${(p) => (p.$ghost ? theme.color.ink : '#fff')};
  border: 1px solid ${(p) => (p.$ghost ? theme.color.line : theme.color.accent)};
  font-family: ${theme.font.body}; font-weight: 700; font-size: 0.85rem; padding: 9px 16px; border-radius: ${theme.layout.radius}; cursor: pointer; &:disabled { opacity: 0.6; }
`;
const Row = styled.div`
  display: flex; align-items: center; gap: 1rem; padding: 0.8rem 0; border-bottom: 1px solid ${theme.color.line}; font-size: 0.93rem;
  .t { flex: 1; } .muted { color: ${theme.color.inkSoft}; }
`;
const Select = styled.select` width: 100%; font-family: ${theme.font.body}; font-size: 0.95rem; padding: 10px 13px; border: 1px solid ${theme.color.line}; border-radius: ${theme.layout.radius}; background: #fff; `;

const SECTIONS = [
  { key: 'overview', label: 'Overview' },
  { key: 'classes', label: 'Classes & students' },
  { key: 'announcements', label: 'Announcements' },
  { key: 'assignments', label: 'Assignments' },
];

function GradeRow({ s, onGraded }) {
  const [grade, setGrade] = useState(s.grade || '');
  const [feedback, setFeedback] = useState(s.feedback || '');
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await gradeSubmission(s.id, grade, feedback); onGraded(); } catch (e) { alert(e.message); } finally { setBusy(false); }
  };
  return (
    <div style={{ borderTop: `1px solid ${theme.color.line}`, padding: '0.8rem 0' }}>
      <div style={{ fontSize: '0.9rem' }}><strong>{s.student_email}</strong>{s.graded_at && <span style={{ color: '#1a7f4b', marginLeft: 8, fontSize: '0.8rem' }}>graded</span>}</div>
      {s.note && <p style={{ margin: '0.3rem 0', color: theme.color.inkSoft, fontSize: '0.9rem' }}>{s.note}</p>}
      {s.file_url && <a href={s.file_url} target="_blank" rel="noreferrer" style={{ color: theme.color.accent, fontWeight: 700, fontSize: '0.85rem' }}>Open submission ↗</a>}
      <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem', flexWrap: 'wrap' }}>
        <input value={grade} onChange={(e) => setGrade(e.target.value)} placeholder="Grade (e.g. A, 78%)"
          style={{ width: 130, padding: '8px 10px', border: `1px solid ${theme.color.line}`, borderRadius: theme.layout.radius, fontFamily: theme.font.body }} />
        <input value={feedback} onChange={(e) => setFeedback(e.target.value)} placeholder="Feedback (optional)"
          style={{ flex: 1, minWidth: 160, padding: '8px 10px', border: `1px solid ${theme.color.line}`, borderRadius: theme.layout.radius, fontFamily: theme.font.body }} />
        <Btn onClick={save} disabled={busy || !grade}>{busy ? 'Saving…' : 'Save grade'}</Btn>
      </div>
    </div>
  );
}

function GradePanel({ a, fmt, onDelete }) {
  const [open, setOpen] = useState(false);
  const [subs, setSubs] = useState([]);
  const load = useCallback(() => fetchSubmissionsForAssignment(a.id).then(setSubs), [a.id]);
  useEffect(() => { if (open) load(); }, [open, load]);
  return (
    <Card>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div><strong>{a.title}</strong> <span style={{ color: theme.color.inkSoft, fontSize: '0.85rem' }}>{a.classes?.name} {a.due_date ? `· due ${fmt(a.due_date)}` : ''}</span></div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <Btn $ghost onClick={() => setOpen(!open)}>{open ? 'Close' : 'Submissions'}</Btn>
          <Btn $ghost onClick={onDelete}>Delete</Btn>
        </div>
      </div>
      {open && (
        <div style={{ marginTop: '0.8rem' }}>
          {subs.length === 0 ? <Empty>No submissions yet.</Empty> : subs.map((s) => <GradeRow key={s.id} s={s} onGraded={load} />)}
        </div>
      )}
    </Card>
  );
}

export default function StaffPortal() {
  const { user, isStaff, loading, ready } = useAuth();
  const [tab, setTab] = useState('overview');
  const [classes, setClasses] = useState([]);
  const [ann, setAnn] = useState([]);
  const [asg, setAsg] = useState([]);

  // class editor
  const [newClass, setNewClass] = useState({ code: '', name: '', department: '', description: '' });
  const [openClass, setOpenClass] = useState(null);
  const [roster, setRoster] = useState([]);
  const [enrolWho, setEnrolWho] = useState(''); // Student ID or email
  // posting
  const [post, setPost] = useState({ title: '', body: '', class_id: '', audience: 'class' });
  const [task, setTask] = useState({ title: '', instructions: '', class_id: '', due_date: '', file_url: '' });
  const [busy, setBusy] = useState(false);

  const load = () => { fetchClasses().then(setClasses); fetchAnnouncements().then(setAnn); fetchAssignments().then(setAsg); };
  useEffect(() => { if (ready && user && isStaff) load(); }, [ready, user, isStaff]);
  useEffect(() => { if (openClass) fetchEnrolments(openClass.id).then(setRoster); }, [openClass]);

  if (!ready) return (<><Navbar /><Note><h2>Portal not connected</h2><p>Add your Supabase keys to enable the portal.</p><Link to="/">Back home</Link></Note><Footer /></>);
  if (loading) return (<><Navbar /><Note><h2>Loading…</h2></Note><Footer /></>);
  if (!user) return (<><Navbar /><Note><h2>Please sign in</h2><p>The staff portal is for institute staff.</p><Link to="/login">Go to login</Link></Note><Footer /></>);
  if (!isStaff) return (<><Navbar /><Note><h2>Staff only</h2><p>This account isn’t marked as staff. Ask an administrator to set your role.</p><Link to="/">Back home</Link></Note><Footer /></>);

  const addClass = async () => { setBusy(true); try { await saveClass(newClass); setNewClass({ code: '', name: '', department: '', description: '' }); load(); } catch (e) { alert(e.message); } finally { setBusy(false); } };
  const delClass = async (id) => { if (!window.confirm('Delete this class?')) return; try { await removeClass(id); if (openClass?.id === id) setOpenClass(null); load(); } catch (e) { alert(e.message); } };
  const doEnrol = async () => { if (!enrolWho) return; setBusy(true); try { await enrolStudent(openClass.id, enrolWho); setEnrolWho(''); fetchEnrolments(openClass.id).then(setRoster); } catch (e) { alert(e.message); } finally { setBusy(false); } };
  const doUnenrol = async (id) => { try { await unenrol(id); fetchEnrolments(openClass.id).then(setRoster); } catch (e) { alert(e.message); } };

  const sendPost = async () => {
    setBusy(true);
    try {
      const row = { ...post };
      if (row.audience === 'class' && !row.class_id) { alert('Pick a class, or choose a different audience.'); setBusy(false); return; }
      if (row.audience !== 'class') row.class_id = null;
      await saveAnnouncement(row); setPost({ title: '', body: '', class_id: '', audience: 'class' }); load();
    } catch (e) { alert(e.message); } finally { setBusy(false); }
  };
  const sendTask = async () => {
    setBusy(true);
    try {
      if (!task.class_id) { alert('Pick a class.'); setBusy(false); return; }
      await saveAssignment(task); setTask({ title: '', instructions: '', class_id: '', due_date: '', file_url: '' }); load();
    } catch (e) { alert(e.message); } finally { setBusy(false); }
  };
  const pickFile = async (e) => { const f = e.target.files?.[0]; if (!f) return; setBusy(true); try { const url = await uploadFile(f); setTask((p) => ({ ...p, file_url: url })); } catch (er) { alert(er.message); } finally { setBusy(false); } };
  const fmt = (d) => (d ? new Date(d).toLocaleDateString() : '');

  return (
    <>
      <PortalShell kind="Staff" sections={SECTIONS} active={tab} onSelect={setTab}>
        {tab === 'overview' && (
          <>
            <H>Overview</H>
            <p style={{ color: theme.color.inkSoft }}>You manage {classes.length} class{classes.length === 1 ? '' : 'es'}, {ann.length} announcement{ann.length === 1 ? '' : 's'} and {asg.length} assignment{asg.length === 1 ? '' : 's'}.</p>
          </>
        )}

        {tab === 'classes' && (
          <>
            <H>Classes & students</H>
            <Card>
              <h3 style={{ marginBottom: '0.8rem' }}>Create a class</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '0.8rem' }}>
                <Field><label>Code</label><input value={newClass.code} onChange={(e) => setNewClass({ ...newClass, code: e.target.value })} placeholder="AGR-101" /></Field>
                <Field><label>Name</label><input value={newClass.name} onChange={(e) => setNewClass({ ...newClass, name: e.target.value })} placeholder="Crop Production" /></Field>
              </div>
              <Field><label>Department</label><input value={newClass.department} onChange={(e) => setNewClass({ ...newClass, department: e.target.value })} /></Field>
              <Field><label>Description</label><textarea value={newClass.description} onChange={(e) => setNewClass({ ...newClass, description: e.target.value })} /></Field>
              <Btn onClick={addClass} disabled={busy || !newClass.name}>Add class</Btn>
            </Card>

            {classes.map((c) => (
              <Card key={c.id}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div><strong>{c.name}</strong> <span style={{ color: theme.color.inkSoft, fontSize: '0.85rem' }}>{c.code} · {c.department}</span></div>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <Btn $ghost onClick={() => setOpenClass(openClass?.id === c.id ? null : c)}>{openClass?.id === c.id ? 'Close' : 'Manage students'}</Btn>
                    <Btn $ghost onClick={() => delClass(c.id)}>Delete</Btn>
                  </div>
                </div>
                {openClass?.id === c.id && (
                  <div style={{ marginTop: '1rem', borderTop: `1px solid ${theme.color.line}`, paddingTop: '1rem' }}>
                    <div style={{ display: 'flex', gap: '0.6rem', marginBottom: '0.4rem' }}>
                      <input value={enrolWho} onChange={(e) => setEnrolWho(e.target.value)} placeholder="Student ID (MTI/2026/001) or email"
                        onKeyDown={(e) => e.key === 'Enter' && doEnrol()}
                        style={{ flex: 1, padding: '9px 12px', border: `1px solid ${theme.color.line}`, borderRadius: theme.layout.radius, fontFamily: theme.font.body }} />
                      <Btn onClick={doEnrol} disabled={busy || !enrolWho}>Enrol</Btn>
                    </div>
                    <p style={{ fontSize: '0.8rem', color: theme.color.inkSoft, marginBottom: '0.8rem' }}>
                      Most students are given a Student ID rather than an email — either works.
                    </p>
                    {roster.length === 0 ? <Empty>No students enrolled yet.</Empty> : roster.map((r) => (
                      <Row key={r.id}>
                        <span className="t">
                          <strong>{r.profiles?.full_name || r.profiles?.student_id || r.profiles?.email}</strong>
                          <div className="muted">{r.profiles?.student_id || r.profiles?.email}</div>
                        </span>
                        <Btn $ghost onClick={() => doUnenrol(r.id)}>Remove</Btn>
                      </Row>
                    ))}
                  </div>
                )}
              </Card>
            ))}
          </>
        )}

        {tab === 'announcements' && (
          <>
            <H>Post an announcement</H>
            <Card>
              <Field><label>Audience</label>
                <Select value={post.audience} onChange={(e) => setPost({ ...post, audience: e.target.value })}>
                  <option value="class">A specific class</option>
                  <option value="students">All students</option>
                  <option value="staff">All staff</option>
                  <option value="all">Everyone</option>
                </Select>
              </Field>
              {post.audience === 'class' && (
                <Field><label>Class</label>
                  <Select value={post.class_id} onChange={(e) => setPost({ ...post, class_id: e.target.value })}>
                    <option value="">Select a class</option>
                    {classes.map((c) => <option key={c.id} value={c.id}>{c.name} ({c.code})</option>)}
                  </Select>
                </Field>
              )}
              <Field><label>Title</label><input value={post.title} onChange={(e) => setPost({ ...post, title: e.target.value })} /></Field>
              <Field><label>Message</label><textarea value={post.body} onChange={(e) => setPost({ ...post, body: e.target.value })} /></Field>
              <Btn onClick={sendPost} disabled={busy || !post.title}>Post announcement</Btn>
            </Card>
            <H>Recent</H>
            {ann.length === 0 ? <Empty>Nothing posted yet.</Empty> : ann.map((a) => (
              <Row key={a.id}><span className="t"><strong>{a.title}</strong><div className="muted">{a.classes?.name || a.audience} · {fmt(a.created_at)}</div></span><Btn $ghost onClick={async () => { await removeAnnouncement(a.id); load(); }}>Delete</Btn></Row>
            ))}
          </>
        )}

        {tab === 'assignments' && (
          <>
            <H>Set an assignment</H>
            <Card>
              <Field><label>Class</label>
                <Select value={task.class_id} onChange={(e) => setTask({ ...task, class_id: e.target.value })}>
                  <option value="">Select a class</option>
                  {classes.map((c) => <option key={c.id} value={c.id}>{c.name} ({c.code})</option>)}
                </Select>
              </Field>
              <Field><label>Title</label><input value={task.title} onChange={(e) => setTask({ ...task, title: e.target.value })} /></Field>
              <Field><label>Instructions</label><textarea value={task.instructions} onChange={(e) => setTask({ ...task, instructions: e.target.value })} /></Field>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.8rem' }}>
                <Field><label>Due date</label><input type="date" value={task.due_date} onChange={(e) => setTask({ ...task, due_date: e.target.value })} /></Field>
                <Field><label>Attachment</label>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    {task.file_url && <a href={task.file_url} target="_blank" rel="noreferrer" style={{ color: theme.color.accent, fontWeight: 700, fontSize: '0.85rem' }}>File ↗</a>}
                    <label style={{ cursor: 'pointer', color: theme.color.accent, fontWeight: 700, fontSize: '0.85rem' }}>{busy ? 'Uploading…' : 'Upload'}<input type="file" onChange={pickFile} style={{ display: 'none' }} /></label>
                  </div>
                </Field>
              </div>
              <Btn onClick={sendTask} disabled={busy || !task.title}>Set assignment</Btn>
            </Card>
            <H>Recent</H>
            {asg.length === 0 ? <Empty>Nothing set yet.</Empty> : asg.map((a) => (
              <GradePanel key={a.id} a={a} fmt={fmt} onDelete={async () => { await removeAssignment(a.id); load(); }} />
            ))}
          </>
        )}
      </PortalShell>
      <Footer />
    </>
  );
}
